import {useCallback, useRef} from 'react'
import {useCustomerNewsletter} from './useScapiCustomApi'
import {useCookieConsent} from './use-cookie-consent'
import {trackEvent} from '../tracking/analytics'

// sign-up source of the newsletter block, the only source that sends the page URL
export const NEWSLETTER_BLOCK_SOURCE = 'Web - Newsletter Block'

/**
 * Newsletter sign-up flow shared by the newsletter forms: executes reCAPTCHA, tracks the
 * `sign_up` event and posts the subscription with the opt-in, the reCAPTCHA token and the source.
 * Sign-ups from the newsletter block also carry the current page URL (`pageUrl`, including all
 * query parameters such as UTMs) when marketing cookies are accepted.
 *
 * Pass the returned `recaptchaRef` to the form's `<Recaptcha>`. `signUp` rejects when reCAPTCHA
 * or the request fails, so each form shows the error its own way.
 *
 * @example
 * const {recaptchaRef, signUp} = useNewsletterSignUp({source: NEWSLETTER_BLOCK_SOURCE})
 *
 * const onSubmit = async ({email}) => {
 *     try {
 *         await signUp({email})
 *         onSuccess()
 *     } catch {
 *         setHasError(true)
 *     }
 * }
 *
 * <Recaptcha ref={recaptchaRef} />
 *
 * @param {Object} options
 * @param {string} options.source - Sign-up source sent to the backend.
 * @returns {{recaptchaRef: React.MutableRefObject, signUp: function({email: string}): Promise}}
 * `signUp` takes the email plus any further subscription fields (e.g. `firstName`, `pregnantDueDate`).
 */
const useNewsletterSignUp = ({source}) => {
    const recaptchaRef = useRef(null)
    const {mutateAsync: subscribeToNewsletter} = useCustomerNewsletter()
    const {marketing} = useCookieConsent()

    const signUp = useCallback(
        async ({email, ...fields}) => {
            const token = await recaptchaRef.current?.execute()
            trackEvent({
                event: 'sign_up',
                event_category: 'newsletter',
                event_action: 'signup',
                email
            })
            return subscribeToNewsletter({
                body: JSON.stringify({
                    email,
                    ...fields,
                    optIn: 1,
                    gRecaptchaResponse: token,
                    source,
                    ...(source === NEWSLETTER_BLOCK_SOURCE &&
                        marketing && {pageUrl: window.location.href})
                })
            })
        },
        [subscribeToNewsletter, source, marketing]
    )

    return {recaptchaRef, signUp}
}

export default useNewsletterSignUp
